<script setup lang="ts">
import { onMounted, ref } from "vue";
import { api, ApiError } from "./lib/api.js";
import { useBoard } from "./stores/board.js";
import LoginView from "./components/LoginView.vue";
import AppShell from "./components/AppShell.vue";
import InviteView from "./components/InviteView.vue";
import type { User } from "@shared/types.js";
import type { UserSettings } from "@shared/settings.js";

const board = useBoard();
const checking = ref(true);
/** An emailed invite's link (`?invite=`): its page stands in for the app, which is not started. */
const invite = new URL(location.href).searchParams.get("invite");
const signedIn = ref<User | null>(null);

onMounted(async () => {
  // A live cookie means we can go straight in; a 401 just means "show login".
  // In without waiting for the board: `start()` has the reader at once, and a
  // view draws its own placeholder until `board.loaded` — the tmux view needs
  // nothing from it, and on a slow connection the board is the long wait.
  try {
    const { user, settings } = await api.get<{ user: User; settings: UserSettings }>("/api/me");
    signedIn.value = user;
    if (!invite) board.start(user, settings).catch((err: unknown) => console.error(err));
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
  } finally {
    checking.value = false;
  }
});
</script>

<template>
  <div v-if="checking" class="empty">…</div>
  <InviteView v-else-if="invite" :token="invite" :user="signedIn" />
  <LoginView v-else-if="!board.me" />
  <AppShell v-else />
</template>
